import { cx } from '@orn/shared-web/utils/cx';
import * as React from 'react';

/**
 * MOPS-local form input. Deliberately separate from
 * `@orn/shared-web/components/Input` — the shared-web primitive is styled for
 * SaaS dashboards (`rounded-xl`, `shadow-xs`, sizeable padding) and cannot be
 * reconciled with the MOPS operator aesthetic (sharp edges, no shadows,
 * dense). Same decision as the MOPS-local `Table`.
 *
 * Minimal API by design: label + hint are the only chrome affordances. No
 * tooltips, no keyboard-shortcut badge, no icons. Add what we need when we
 * need it; do not port the shared-web surface area wholesale.
 *
 * Value-based `onChange` (not event-based) to match the react-aria convention
 * shared-web Input uses. Keeps consumer call sites drop-in compatible.
 */
export interface IInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'size' | 'className'> {
  /** Visible label rendered above the field. */
  label?: string;
  /** Helper / error text rendered below the field. */
  hint?: string;
  /** When true, the field is rendered in an error state and `hint` turns red. */
  isInvalid?: boolean;
  /** Value-based change handler, mirrors `react-aria`'s TextField contract. */
  onChange?: (value: string) => void;
  /** Outer wrapper className — use for width / grid sizing. */
  className?: string;
  /** Input element className — use sparingly for one-off overrides. */
  inputClassName?: string;
  /**
   * Leading icon rendered inside the field's left inset. Use when the field
   * carries no `label` and the icon is what names it (a search box in a dense
   * rail); pair it with `aria-label` so the control is still announced.
   */
  icon?: React.ComponentType<{ className?: string }>;
}

const Input = ({
  label,
  hint,
  isInvalid = false,
  onChange,
  className,
  inputClassName,
  icon: Icon,
  id: idProp,
  ...inputProps
}: IInputProps) => {
  const generatedId = React.useId();
  const id = idProp ?? generatedId;
  const hintId = hint ? `${id}-hint` : undefined;

  return (
    <div className={cx('flex flex-col gap-1', className)}>
      {label && (
        <label htmlFor={id} className="text-fg-secondary text-xs font-medium uppercase tracking-wider">
          {label}
        </label>
      )}
      <div className="relative">
        {Icon && (
          <Icon className="text-fg-quaternary pointer-events-none absolute left-2 top-1/2 size-4 -translate-y-1/2" />
        )}
        <input
          id={id}
          aria-invalid={isInvalid || undefined}
          aria-describedby={hintId}
          onChange={(e) => onChange?.(e.target.value)}
          className={cx(
            'border-border-primary bg-bg-primary text-fg-primary placeholder:text-fg-quaternary w-full border px-2.5 py-1.5 font-mono text-sm transition-colors',
            'focus-visible:outline-border-brand focus-visible:outline-2 focus-visible:-outline-offset-1',
            Icon && 'pl-8',
            isInvalid && 'border-fg-error-primary',
            inputClassName
          )}
          {...inputProps}
        />
      </div>
      {hint && (
        <span id={hintId} className={cx('text-xs', isInvalid ? 'text-fg-error-primary' : 'text-fg-tertiary')}>
          {hint}
        </span>
      )}
    </div>
  );
};

export default Input;
